import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import xboxHistory from "../assets/xboxHistory.png";
import api from "../utils/api";
import AccountSidebar from "./AccountSidebar";

const OrderDetails = () => {
  const { orderId } = useParams();
  const { token } = useAuth();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoized API call to avoid unnecessary re-renders
  const fetchOrderDetails = useCallback(async () => {
    if (!token) {
      console.error("No auth token found.");
      setError("Authentication token missing.");
      return;
    }

    try {
      const { data } = await api.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrderDetails(data);
    } catch (err) {
      setError("Failed to fetch order details. Please try again later.");
      console.error("Error fetching order details:", err.message);
    } finally {
      setLoading(false);
    }
  }, [orderId, token]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  if (loading) return <div className="p-6 bg-black text-white rounded-lg">Loading order details...</div>;
  if (error) return <div className="p-6 bg-red-500 text-white rounded-lg">{error}</div>;

  const { price, quantity, type, walletUsed, createdAt, orderId: OrderDBId } = orderDetails;

  return (
    <div className="flex flex-col lg:flex-row">
      <AccountSidebar className="lg:w-1/4" />
      <div className="flex-1 p-6 bg-white text-black rounded-lg border border-primary mr-0 lg:mr-36">
        {/* Order Header */}
        <OrderHeader
          orderId={OrderDBId}
          quantity={quantity}
          createdAt={createdAt}
          price={price}
        />

        {/* Product Section */}
        <ProductSection type={type} price={price} quantity={quantity} />

        {/* Wallet Used Section */}
        {walletUsed > 0 && (
          <WalletUsedSection walletUsed={walletUsed} />
        )}
      </div>
    </div>
  );
};

const OrderHeader = ({ orderId, quantity, createdAt, price }) => (
  <div className="bg-primary flex border-2 border-black flex-col lg:flex-row justify-between items-start lg:items-center px-4 py-10 rounded-lg mb-6">
    <div className="mb-4 lg:mb-0">
      <div className="text-lg font-bold text-black">{orderId}</div>
      <div className="text-sm text-black">
        {quantity}K • Order Placed on {new Date(createdAt).toLocaleDateString()} at{" "}
        {new Date(createdAt).toLocaleTimeString()}
      </div>
    </div>
    <div className="text-lg font-bold">${price}</div>
  </div>
);

const ProductSection = ({ type, price, quantity }) => (
  <div>
    <div className="text-md font-bold mb-3">Product (01)</div>
    <div className="lg:hidden bg-gray-800 p-4 rounded-lg mb-6">
      <MobileProductCard type={type} price={price} quantity={quantity} />
    </div>
    <div className="hidden lg:block overflow-x-auto">
      <ProductTable type={type} price={price} quantity={quantity} />
    </div>
  </div>
);

const MobileProductCard = ({ type, price, quantity }) => (
  <div className="flex items-center mb-4">
    <img src={xboxHistory} alt="Xbox" className="w-10 h-10 object-cover rounded-lg mr-4" />
    <div>
      <div className="font-bold text-white">{type}</div>
      <div className="text-sm text-gray-400">{type} Coins</div>
    </div>
    <div className="text-sm text-gray-400">
      <div>
        <span className="text-white">Price: </span>${price}
      </div>
      <div>
        <span className="text-white">Quantity: </span>{quantity}K
      </div>
      <div>
        <span className="text-white">Sub-Total: </span>${price}
      </div>
    </div>
  </div>
);

const ProductTable = ({ type, price, quantity }) => (
  <table className="w-full table-auto bg-gray-800 rounded-lg overflow-hidden">
    <thead>
      <tr className="text-left text-sm uppercase bg-gray-700 text-gray-400">
        <th className="p-4">Products</th>
        <th className="p-4">Price</th>
        <th className="p-4">Quantity</th>
        <th className="p-4">Sub-Total</th>
      </tr>
    </thead>
    <tbody>
      <OrderTableRow imgSrc={xboxHistory} type={type} price={price} quantity={quantity} />
    </tbody>
  </table>
);

const OrderTableRow = ({ imgSrc, type, price, quantity }) => (
  <tr className="text-white">
    <td className="p-4 flex flex-col lg:flex-row items-center space-x-4">
      <img src={imgSrc} alt="Product" className="w-10 h-10 object-cover rounded-lg mb-2 lg:mb-0" />
      <div>
        <div className="font-bold">{type}</div>
        <div className="text-sm text-gray-400">{type} Coins</div>
      </div>
    </td>
    <td className="p-4">${price}</td>
    <td className="p-4">{quantity}K</td>
    <td className="p-4">${price}</td>
  </tr>
);

const WalletUsedSection = ({ walletUsed }) => (
  <div className="mt-4 bg-gray-800 p-4 rounded-lg">
    <span className="text-gray-400">Wallet Used:</span>{" "}
    <span className="font-bold text-primary">${walletUsed.toFixed(2)}</span>
  </div>
);

export default OrderDetails;
